import { useGetFilesUrlsQuery } from 'store/services/documentStorage/googleCloudStorage';
import { useGetUsableViewFormItemsQuery, useGetViewWidgetSettingsQuery } from 'store/services/flow/viewFormItems';
import { useUpdateAgendaRecordMutation, useRunActionsOnAgendaRecordMutation } from 'store/services/flow/agendaRecords';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { styled, TableBody as WidgetTableBody } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import type { Row } from '@tanstack/react-table';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import type { AgendaRecordItem, DataSchema, GetAgendaRecordsRes, Schema, VisibilityResult } from 'types';
import type { ViewItem, ViewItemFile } from 'types/catalogViewItem';
import { CHANGE_BEHAVIOR_OPTION } from 'types/enums';
import { getAttributeValue, getPortalSafeValue, initialAttributeValues } from 'utils/agendaRecord/agendaRecord';
import { handleRedirectActions } from 'utils/agendaRecord/handleRedirectActions';
import { validateMessage } from 'utils/agendaRecord/validateMessage';
import { handleSaveError } from 'utils/agendaRecord/handleSaveError';
import { getBuildViewModel } from 'utils/catalogueWidget/getBuildViewModel';
import { getCatalogueImages } from 'utils/catalogueWidget/getCatalogueImages';
import { toLowerCamelCase } from 'utils/string';
import { buildAttributePayload, processFormAttributes } from 'utils/table/user/contextMenu';
import { useShoppingCart } from 'contexts';
import { Loader } from 'components/common';
import { CatalogueWidgetItem } from './CatalogueWidgetItem';

const CatalogueWrapper = styled('div')(() => ({
  display: 'grid',
  gap: '16px',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gridAutoRows: '1fr',
  alignItems: 'stretch',
}));

interface CatalogueWidgetProps<T extends keyof P, P extends DataSchema> {
  rows: Row<Schema<T, P>>[];
  viewWidgetId: string;
  viewId: string;
  dataSchema: DataSchema;
  visibilityResults?: Record<string, VisibilityResult>;
  selectedRows: string[];
  handleRowSelect: (rowId: string, isChecked: boolean) => void;
  dataD?: Schema<T, P>[];
  setCatalogData?: Dispatch<SetStateAction<GetAgendaRecordsRes<AgendaRecordItem[]>>>;
  onEntireRowClick?: (row: Schema<T, P>) => void;
}

export const CatalogueWidget = <T extends keyof P, P extends DataSchema>({
  rows,
  viewWidgetId,
  viewId,
  dataSchema,
  visibilityResults,
  selectedRows,
  handleRowSelect,
  dataD,
  setCatalogData,
  onEntireRowClick,
}: CatalogueWidgetProps<T, P>) => {
  const { t } = useTranslation();
  const { agendaId } = useParams();
  const { data: usableItems } = useGetUsableViewFormItemsQuery({ viewId });
  const { data: viewWidget } = useGetViewWidgetSettingsQuery({ viewWidgetId });

  const navigate = useNavigate();
  const { triggerRefetch: refreshCart } = useShoppingCart();

  const catalogueImages = useMemo(
    () =>
      getCatalogueImages({
        usableItems,
        viewWidget,
        rows: rows,
      }),
    [usableItems, viewWidget, rows]
  );

  const { data: { fileUrls: filesWithUrl = [] } = {}, isFetching: isFetchingUrls } = useGetFilesUrlsQuery(
    {
      ids: catalogueImages.map(({ fileId }) => fileId ?? '').filter((id) => id !== ''),
    },
    { skip: catalogueImages.length === 0 }
  );

  const catalogueImagesWithUrls: ViewItemFile[] = useMemo(
    () =>
      catalogueImages.map((file) => ({
        ...file,
        file: filesWithUrl?.find((f) => f.id === file.fileId),
      })),
    [catalogueImages, filesWithUrl]
  );

  const getVisibilityResults = useCallback(
    (rowId: string) => {
      return Object.fromEntries(
        Object.entries(visibilityResults ?? {})
          ?.filter(([key]) => key.includes(rowId))
          .map(([key, value]) => {
            const formItemKey = key.split('.')?.[1];
            return [formItemKey, value];
          })
      );
    },
    [visibilityResults]
  );
  const modelsRef = useRef<{ rowId: string; viewModel: ViewItem[] }[]>([]);
  const methods = useForm({ defaultValues: { items: {} } });
  const defaultValuesRef = useRef({});

  const [updateAgendaRecord] = useUpdateAgendaRecordMutation();
  const [runActionsOnAgendaRecord] = useRunActionsOnAgendaRecordMutation();

  useEffect(() => {
    const initialItems: Record<string, Record<string, unknown>> = {};
    const buildViewModels: { rowId: string; viewModel: ViewItem[] }[] = [];

    rows.forEach((row) => {
      const viewModel = getBuildViewModel({
        row,
        viewWidget,
        usableItems,
        catalogueImagesWithUrls,
        dataSchema,
        visibilityResults: getVisibilityResults(row.original.id),
      });

      const valuePairs = viewModel.filter((v) => v?.kind !== 'Button').map((v) => [v.name, v.value ?? '']);

      buildViewModels.push({ rowId: row.original.id, viewModel });
      initialItems[row.original.id] = Object.fromEntries(valuePairs);
    });

    modelsRef.current = buildViewModels;
    defaultValuesRef.current = initialItems;
  }, [rows, viewWidget, usableItems, catalogueImagesWithUrls, dataSchema, visibilityResults, getVisibilityResults]);

  useEffect(() => {
    methods.reset({ items: defaultValuesRef.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(defaultValuesRef.current), JSON.stringify(modelsRef.current), catalogueImagesWithUrls?.length, JSON.stringify(filesWithUrl)]);

  const handleRecordSubmit = useCallback(
    async (recordId: string, formItemAgendaId: string, viewItem: ViewItem, viewModel?: ViewItem[]) => {
      const items = viewModel?.filter((model) => model.isEditable);

      if (selectedRows?.length > 0) {
        const attributes = (rowId: string) =>
          (items ?? [])
            ?.map((item) => {
              const values = methods.getValues(`items.${rowId}` as 'items') as Record<string, unknown>;
              const key = item.title ?? (item.name as string);
              return buildAttributePayload(item.id, item.kind, values[key], item.itemSchema?.formItemDefinition);
            })
            .filter((attribute) => attribute !== null);

        try {
          await Promise.all(
            selectedRows.map((rowIdentifier) => {
              updateAgendaRecord({
                agendaId,
                recordId: rowIdentifier,
                body: {
                  status: 'Active',
                  attributes: [
                    ...attributes(rowIdentifier),
                    ...processFormAttributes((dataD as unknown as GetAgendaRecordsRes<AgendaRecordItem[]>)?.items, viewItem.id, recordId),
                  ],
                },
              });
            })
          );

          toast.success(t('toast.updated.record'));
          refreshCart();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
          toast.error(t('common.error'));
        }
        return;
      }

      const values = methods.getValues(`items.${recordId}` as 'items') as Record<string, unknown>;

      const attributes = (items ?? [])
        ?.map((item) => {
          const key = item.title ?? (item.name as string);
          return buildAttributePayload(item.id, item.kind, values[key], item.itemSchema?.formItemDefinition);
        })
        .filter((attribute) => attribute !== null);

      const res = await updateAgendaRecord({
        agendaId: formItemAgendaId,
        recordId,
        body: {
          status: 'Active',
          attributes: [
            ...attributes,
            ...processFormAttributes((dataD as unknown as GetAgendaRecordsRes<AgendaRecordItem[]>)?.items, viewItem.id, recordId),
          ],
        },
      });

      if (!('error' in res)) {
        try {
          validateMessage(res.data);

          toast.success(t('toast.updated.record'));
          refreshCart();

          handleRedirectActions(res.data, navigate);
        } catch (error) {
          handleSaveError(error, {
            onActionError: () => toast.error(t('toast.error.action')),
            onUnknownError: () => toast.error(t('common.error')),
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRows, refreshCart]
  );

  const handleRunActionsWithoutSaving = useCallback(
    async (recordId: string, formItemAgendaId: string, viewModel?: ViewItem[], buttonViewFormItemId?: string) => {
      const items = viewModel?.filter((model) => model.kind && (model.isEditable || model.id === buttonViewFormItemId));
      const values = methods.getValues(`items.${recordId}` as 'items') as Record<string, unknown>;

      const attributes = (items ?? [])
        .filter((item) => item.kind && item.id)
        .map((item) => {
          const type = item.kind as string;
          const isClickedButton = type === 'Button' && item.id === buttonViewFormItemId;
          const value = isClickedButton
            ? true
            : (values[item.title ?? (item.name as string)] as string | number | boolean | Record<string, string>[]);
          const definition = item.itemSchema?.formItemDefinition;
          const payloadValue = definition ? getPortalSafeValue(definition, value) : value;

          const attributeName = `${toLowerCamelCase(type)}AttributeInput`;
          return {
            formItemId: item.id,
            ...initialAttributeValues,
            [attributeName]:
              (payloadValue !== undefined && payloadValue !== null) || type === 'Button' ? getAttributeValue(type, payloadValue) : null,
          };
        });

      const res = await runActionsOnAgendaRecord({
        agendaId: formItemAgendaId,
        body: {
          status: 'Active',
          attributes,
        },
        recordId,
      });

      if (!('error' in res)) {
        toast.success(t('toast.updated.record'));
        refreshCart();

        handleRedirectActions(res.data, navigate);
      }
    },
    [methods, runActionsOnAgendaRecord, t, refreshCart, navigate]
  );

  const buttonActionHandler = useCallback(
    (viewItem: ViewItem, row: Row<Schema<T, P>>, viewModel?: ViewItem[]) => {
      // eslint-disable-next-line
      switch ((viewItem?.itemSchema?.formItemDefinition as any)?.button?.changeBehavior) {
        case CHANGE_BEHAVIOR_OPTION.NONE: {
          onEntireRowClick?.(row.original);
          return;
        }
        case CHANGE_BEHAVIOR_OPTION.NOTIFY_USER_TO_SAVE:
          // @todo implement notify behaviour if it's necessarry
          return;
        case CHANGE_BEHAVIOR_OPTION.SAVE_REQUIRED:
          setTimeout(() => handleRecordSubmit?.(row.original.identifier, row.original.agendaId ?? '', viewItem, viewModel), 500);
          return;
        case CHANGE_BEHAVIOR_OPTION.CALCULATE:
          // @todo implement calculate behaviour if it's necessarry
          return;
        case CHANGE_BEHAVIOR_OPTION.RUN_ACTIONS_WITHOUT_SAVING:
          setTimeout(() => handleRunActionsWithoutSaving(row.original.identifier, row.original.agendaId ?? '', viewModel, viewItem.id), 500);
          return;
        default:
          return null;
      }
    },
    [handleRecordSubmit, handleRunActionsWithoutSaving, onEntireRowClick]
  );

  if (isFetchingUrls) {
    return <Loader classname="flex justify-center" />;
  }

  return (
    <WidgetTableBody style={{ width: '100%', padding: '16px' }}>
      <SortableContext items={rows.map((row) => row.original.identifier)} strategy={verticalListSortingStrategy}>
        <FormProvider {...methods}>
          <CatalogueWrapper>
            {rows.map((row) => (
              <CatalogueWidgetItem
                key={row.original.id}
                row={row}
                viewModel={modelsRef.current?.find((model) => model.rowId === row.original.id)?.viewModel}
                selectedRows={selectedRows}
                handleRowSelect={handleRowSelect}
                buttonActionHandler={buttonActionHandler}
                setCatalogData={setCatalogData}
              />
            ))}
          </CatalogueWrapper>
        </FormProvider>
      </SortableContext>
    </WidgetTableBody>
  );
};
